import { createFileRoute, Outlet, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ClubSidebar, type SelectedView } from "../../../../components/ClubSidebar";
import { Avatar } from "../../../../components/ui";
import { useQueueManager } from "../../../../hooks/useQueueManager";
import { trpc } from "../../../../lib/trpc";

export const Route = createFileRoute("/app/clubs/$slug")({
  component: ClubSlugLayout,
});

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

function ClubSlugLayout() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const { data: myClubs, isLoading: isMyClubsLoading } = trpc.club.getMyClubs.useQuery();
  const { data: club } = trpc.club.get.useQuery({ slug });
  const membership = myClubs?.find((c) => c.slug === slug);
  const isMember = !!membership;

  // Detect subroutes
  const matchRoute = useMatchRoute();
  const isSettings = !!matchRoute({
    to: "/app/clubs/$slug/settings",
    params: { slug },
  });
  const isMembersRoute = !!matchRoute({
    to: "/app/clubs/$slug/members",
    params: { slug },
  });
  const isMatchesRoute = !!matchRoute({
    to: "/app/clubs/$slug/matches",
    params: { slug },
  });
  const isStatsRoute = !!matchRoute({
    to: "/app/clubs/$slug/stats",
    params: { slug },
  });
  const isAdminRoute = !!matchRoute({
    to: "/app/clubs/$slug/admin",
    params: { slug },
    fuzzy: true,
  });
  const isQueueRoute = !!matchRoute({
    to: "/app/clubs/$slug/queues/$queueSlug",
    params: { slug },
    fuzzy: true,
  });

  const { setActiveClub } = useQueueManager();

  const selected: SelectedView = useMemo(() => {
    if (isMembersRoute) return { type: "members" };
    if (isMatchesRoute) return { type: "matches" };
    if (isStatsRoute) return { type: "stats" };
    if (isAdminRoute) return { type: "admin" };
    if (isSettings) return { type: "settings" };
    // Queue sub-routes intentionally highlight Overview (queues are children of overview)
    if (isQueueRoute) return { type: "overview" };
    return { type: "overview" };
  }, [isMembersRoute, isMatchesRoute, isStatsRoute, isAdminRoute, isSettings, isQueueRoute]);

  const handleSelect = useCallback(
    (item: SelectedView) => {
      const routes: Record<string, string> = {
        overview: "/app/clubs/$slug",
        members: "/app/clubs/$slug/members",
        matches: "/app/clubs/$slug/matches",
        stats: "/app/clubs/$slug/stats",
        admin: "/app/clubs/$slug/admin",
        settings: "/app/clubs/$slug/settings",
      };
      void navigate({ to: routes[item.type], params: { slug } });
    },
    [navigate, slug],
  );

  // Set active club for navbar queue manager
  useEffect(() => {
    if (membership?.id) {
      setActiveClub(membership.id, slug);
    }
  }, [membership?.id, slug, setActiveClub]);

  // Set page title
  useEffect(() => {
    const clubName = club?.name ?? membership?.name ?? slug;
    document.title = `${clubName} - Rivaled`;
    return () => {
      document.title = "Rivaled";
    };
  }, [club?.name, membership?.name, slug]);

  // Mobile drawer state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const closeMobileSidebar = useCallback(() => setIsMobileSidebarOpen(false), []);

  const handleSelectAndClose = useCallback(
    (item: SelectedView) => {
      handleSelect(item);
      setIsMobileSidebarOpen(false);
    },
    [handleSelect],
  );

  // Loading
  if (isMyClubsLoading && !isSettings) {
    return <ClubLayoutSkeleton />;
  }

  return (
    <div className="flex h-full w-full">
      {/* Desktop sidebar — always visible on lg+ */}
      {club && (
        <div className="hidden lg:flex h-full">
          <ClubSidebar
            club={club}
            slug={slug}
            isMember={isMember}
            role={membership?.role}
            clubId={membership?.id}
            selected={selected}
            onSelect={handleSelect}
          />
        </div>
      )}

      {/* Mobile sidebar drawer — same layout as desktop */}
      {club && isMobileSidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 hidden md:block lg:hidden"
            onClick={closeMobileSidebar}
          />
          <div className="fixed inset-y-0 left-0 z-50 hidden md:block lg:hidden">
            <ClubSidebar
              club={club}
              slug={slug}
              isMember={isMember}
              role={membership?.role}
              clubId={membership?.id}
              selected={selected}
              onSelect={handleSelectAndClose}
              onClose={closeMobileSidebar}
            />
          </div>
        </>
      )}

      <div className="flex flex-col flex-1 min-w-0">
        {/* Mobile club header */}
        <MobileClubHeader
          clubName={club?.name ?? membership?.name ?? slug}
          avatarUrl={club?.avatarUrl ?? null}
          onMenuToggle={() => setIsMobileSidebarOpen((p) => !p)}
          isSidebarOpen={isMobileSidebarOpen}
        />
        <div className="flex-1 min-w-0 overflow-y-auto bg-surface-900">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile Club Header
// ---------------------------------------------------------------------------

function MobileClubHeader({
  clubName,
  avatarUrl,
  onMenuToggle,
  isSidebarOpen,
}: {
  clubName: string;
  avatarUrl: string | null;
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
}) {
  return (
    <div className="flex items-center gap-3 px-3 h-12 bg-surface-850 border-b border-surface-800 shrink-0 lg:hidden">
      {/* Tablet: Hamburger only */}
      <button
        onClick={onMenuToggle}
        className="hidden md:flex items-center justify-center w-8 h-8 rounded-lg text-surface-300 hover:text-white hover:bg-surface-700/30 transition-colors"
      >
        {isSidebarOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </button>

      <div className="flex items-center gap-2.5 min-w-0">
        {/* Avatar — mobile only (sidebar already shows it on md+) */}
        <div className="md:hidden">
          <Avatar
            size="xs"
            variant="rounded"
            src={avatarUrl}
            initials={clubName.charAt(0)}
            alt={clubName}
          />
        </div>
        <span className="text-sm font-bold text-white truncate">{clubName}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full-page skeleton
// ---------------------------------------------------------------------------

function ClubLayoutSkeleton() {
  return (
    <div className="flex h-full w-full">
      {/* Sidebar skeleton (desktop) */}
      <div className="hidden lg:flex flex-col w-72 min-w-72 h-full bg-surface-850 border-r border-surface-800">
        <div className="h-32 bg-surface-800/40 animate-pulse" />
        <div className="px-5 -mt-8 relative z-10">
          <div className="w-16 h-16 rounded-xl bg-surface-700/50 animate-pulse ring-[3px] ring-surface-850" />
          <div className="h-5 w-32 rounded bg-surface-700/40 animate-pulse mt-3" />
          <div className="h-3 w-full rounded bg-surface-700/25 animate-pulse mt-2" />
          <div className="h-3 w-2/3 rounded bg-surface-700/25 animate-pulse mt-1" />
        </div>
        <div className="mx-5 my-4 h-px bg-surface-800" />
        <div className="px-3 space-y-2">
          <div className="h-10 rounded-lg bg-surface-700/20 animate-pulse" />
          <div className="h-10 rounded-lg bg-surface-700/20 animate-pulse" />
          <div className="h-10 rounded-lg bg-surface-700/20 animate-pulse" />
        </div>
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex-1 px-6 py-6 space-y-4 bg-surface-900">
          <div className="h-8 w-48 rounded bg-surface-700/40 animate-pulse" />
          <div className="h-4 w-72 rounded bg-surface-700/30 animate-pulse" />
          <div className="h-32 w-full rounded-xl bg-surface-700/20 animate-pulse" />
        </div>
      </div>
    </div>
  );
}
